// Service worker registration (client/sw.js, served at /sw.js, root scope).
// Only on a secure context — anywhere else it is skipped silently. Never on the
// start-up path: register() runs after the page's load event and its failure is
// swallowed (the worker only ever saves downloads, nothing waits for it).
// No local CA (owner rule 2026-10-12): the worker waits for the HOST's valid
// certificate. On a self-signed / invalid one the page is still a secure
// context but the browser rejects register() (SecurityError: "An SSL
// certificate error occurred when fetching the script") — kept as swState
// `refused:<error name>`, one console.info, never retried (the next load asks
// again; the cert does not fix itself mid-page).
// `?sw=off` is the debugging switch: every registration of this origin is
// removed and this app's caches (vrm<N>-*) are deleted; nothing registers.
const PREFIX_RE = /^vrm\d+-/;

// 'pending' (before load) | 'registered' | 'refused:<name>' | 'insecure' (no
// secure context / no serviceWorker) | 'off' (?sw=off) — read-only for importers
export let swState = 'pending';

// env (tests): { nav, loc, win, secure, cachesApi, log } — defaults are the page's
export function registerSw(env = {}) {
  const nav = env.nav || (typeof navigator !== 'undefined' ? navigator : null);
  const loc = env.loc || (typeof location !== 'undefined' ? location : null);
  const win = env.win || (typeof window !== 'undefined' ? window : null);
  const secure = env.secure != null ? env.secure : !!(typeof isSecureContext !== 'undefined' && isSecureContext);
  const cachesApi = env.cachesApi || (typeof caches !== 'undefined' ? caches : null);
  const log = env.log || ((m) => console.info(m));
  try {
    if (!nav || !nav.serviceWorker || !loc || !win || !secure) { swState = 'insecure'; return { state: 'skipped' }; }
    if (new URLSearchParams(loc.search).get('sw') === 'off') {
      swState = 'off';
      const off = Promise.resolve(nav.serviceWorker.getRegistrations())
        .then((regs) => Promise.all(regs.map((r) => r.unregister())))
        .then(() => (cachesApi ? cachesApi.keys() : []))
        .then((names) => Promise.all(names.filter((n) => PREFIX_RE.test(n)).map((n) => cachesApi.delete(n))))
        .catch(() => {});
      return { state: 'off', done: off };
    }
    const go = () => Promise.resolve()
      .then(() => nav.serviceWorker.register('/sw.js', { scope: '/' }))
      .then((reg) => { swState = 'registered'; return reg; }, (e) => {
        swState = 'refused:' + ((e && e.name) || 'Error');
        log(`sw: not registered (${swState}${e && e.message ? ': ' + e.message : ''}) — downloads go uncached`);
        return null;
      });
    if (win.document && win.document.readyState === 'complete') return { state: 'registering', done: go() };
    let done;
    const p = new Promise((resolve) => { done = resolve; });
    win.addEventListener('load', () => done(go()), { once: true });
    return { state: 'waiting', done: p };
  } catch {
    swState = 'insecure';
    return { state: 'skipped' };
  }
}

if (typeof window !== 'undefined') registerSw();
